import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Context from "../../Context";
import constant, { statuses } from "../../constants";
import { validate } from "../../util";
import {
  ScrollArea,
  Avatar,
  AvatarImage,
  useToast,
  Spinner,
} from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";

// React Icons (consistent Feather & Material & Ionicons style)
import {
  FiSearch,
  FiTrash2,
  FiShoppingCart,
  FiLayers,
  FiArchive,
  FiClock,
  FiTruck,
  FiCheckCircle,
  FiAlertCircle,
  FiMapPin,
  FiUser,
  FiPhone,
  FiCreditCard,
  FiCheck,
  FiX,
  FiPlus,
} from "react-icons/fi";
import { IoReceiptOutline } from "react-icons/io5";
import { FaCheckCircle, FaTimes } from "react-icons/fa";

// ── Delivery Channels Configuration ───────────────────────────────────────────
const DELIVERY_CHANNELS = [
  {
    id: "online",
    title: "เช่าพื้นที่พร้อมบริการฯ",
    subTitle: "ฝากสลากในระบบ ปลอดภัย ไม่ต้องกลัวหาย",
    icon: <FiLayers className="text-blue text-lg" />,
  },
  {
    id: "mail",
    title: "ส่งด่วน EMS",
    subTitle: "จัดส่งสลากใบจริงถึงบ้าน",
    icon: <FiTruck className="text-emerald-600 text-lg" />,
  },
  {
    id: "offline",
    title: "ลอตเตอรี่ 80 บาท",
    subTitle: "สิทธิ์ซื้อสลากราคาพิเศษ (สูงสุด 20 ใบ/งวด)",
    icon: <IoReceiptOutline className="text-amber-600 text-lg" />,
  },
];

const EXPIRE_PRESETS = [15, 30, 60, 1440];

// ── Main Page Component ────────────────────────────────────────────────────────
export const CreateOrder = () => {
  const { toast } = useToast();
  const { userId } = useParams();
  const { callApi, navigate } = Context();
  const [selectBlocks, setSelectBlocks] = useState(true);
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    getUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const getUserInfo = async () => {
    setLoadingUser(true);
    const { status, data } = await callApi(
      "users-getUserInfoAdmin",
      { userId },
      { notShowLoading: true }
    );

    if (status && data) {
      setUser(data);
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center">
            <FaTimes /> ไม่พบข้อมูลผู้ใช้งาน
          </div>
        ),
      });
      navigate("/users");
    }
    setLoadingUser(false);
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-12 min-w-0 px-4">
      {/* Header */}
      <Header
        header="Orders"
        subHeader={`สร้างคำสั่งซื้อสลาก / ลูกค้า: ${userId}`}
        icon={<IoReceiptOutline className="text-blue" />}
      />

      {loadingUser ? (
        <div className="flex flex-col h-[400px] justify-center items-center gap-3 bg-white rounded-md shadow-sm border border-[#E5E7E9] mt-4">
          <Spinner />
          <p className="text-sm font-medium text-slate-500">กำลังโหลดข้อมูลลูกค้า...</p>
        </div>
      ) : user ? (
        <div className="mt-4 space-y-4">
          {/* User Profile Banner */}
          <UserProfileBanner user={user} />

          {/* Mode Tabs Switcher */}
          <div className="bg-white rounded-md shadow-sm border border-[#E5E7E9] p-2 flex gap-2">
            <button
              type="button"
              className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-md font-semibold text-sm transition-all duration-200 ${
                selectBlocks
                  ? "bg-blue text-white shadow-md"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
              onClick={() => setSelectBlocks(true)}
            >
              <FiLayers className="text-lg" />
              <div className="text-left">
                <p className="leading-tight font-bold">สลากในระบบ (หน้าบ้าน)</p>
                <p className={`text-xs ${selectBlocks ? "text-blue-100" : "text-slate-400"}`}>
                  ค้นหาจากแผงสลาก ตัดสต็อกพร้อมคิดราคาอัตโนมัติ
                </p>
              </div>
            </button>

            <button
              type="button"
              className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-md font-semibold text-sm transition-all duration-200 ${
                !selectBlocks
                  ? "bg-blue text-white shadow-md"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
              onClick={() => setSelectBlocks(false)}
            >
              <FiArchive className="text-lg" />
              <div className="text-left">
                <p className="leading-tight font-bold">สลากนอกระบบ (หลังบ้าน)</p>
                <p className={`text-xs ${!selectBlocks ? "text-blue-100" : "text-slate-400"}`}>
                  ค้นหาจากคลังสลากที่ยังไม่ขึ้นแผง / สั่งซื้อตามเล่ม
                </p>
              </div>
            </button>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-md shadow-sm border border-[#E5E7E9] overflow-hidden">
            {selectBlocks ? (
              <LotteryPicker user={user} />
            ) : (
              <NonBlockLotteryPicker user={user} />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ── User Profile Banner Component ─────────────────────────────────────────────
const UserProfileBanner = ({ user }) => {
  const isRemoved = user.status === statuses.REMOVED;
  const isInactive = user.status === statuses.INACTIVE;

  const statusColor = isRemoved
    ? "bg-rose-500"
    : isInactive
    ? "bg-amber-500"
    : "bg-emerald-500";

  const statusText = isRemoved
    ? "ถูกลบ/ระงับ"
    : isInactive
    ? "ปิดใช้งาน"
    : "ปกติ (Active)";

  const shippingAddr = user?.address?.addr
    ? `${user.address.addr} ${user.address.province || ""} ${user.address.zipcode || ""}`.trim()
    : "ไม่มีข้อมูลที่อยู่จัดส่ง";

  return (
    <div className="bg-white rounded-md shadow-sm border border-[#E5E7E9] p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar className="w-16 h-16 border-2 border-slate-100 shadow-sm">
            <AvatarImage src={user?.profileImage || "https://github.com/shadcn.png"} />
          </Avatar>
          <span
            className={`w-4 h-4 rounded-full border-2 border-white absolute bottom-0 right-0 ${statusColor}`}
            title={statusText}
          />
        </div>

        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-800">
              {user?.firstName} {user?.lastName}
            </h2>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-semibold text-white ${statusColor}`}
            >
              {statusText}
            </span>
            {user?.userNo && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono font-bold">
                No. {user.userNo}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
            <span className="flex items-center gap-1 font-mono">
              <FiUser className="text-slate-400" /> ID: {user?.userId}
            </span>
            <span className="flex items-center gap-1 font-mono">
              <FiPhone className="text-slate-400" /> {user?.phone || "-"}
            </span>
            {user?.bank?.name && (
              <span className="flex items-center gap-1">
                <FiCreditCard className="text-slate-400" /> {user.bank.name} - {user.bank.accNo} ({user.bank.accName})
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-xs text-slate-600 max-w-md w-full md:w-auto">
        <div className="flex items-center gap-1.5 font-semibold text-slate-700 mb-0.5">
          <FiMapPin className="text-emerald-600" /> ที่อยู่จัดส่งหลัก
        </div>
        <p className="truncate text-slate-500" title={shippingAddr}>
          {shippingAddr}
        </p>
      </div>
    </div>
  );
};

// ── Tab 1: On-Block Picker Component (สลากในระบบ) ─────────────────────────────
const LotteryPicker = ({ user }) => {
  const { toast } = useToast();
  const { callApi, configs } = Context();

  const [lottoNumber, setLottoNumber] = useState("");
  const [searchType, setSearchType] = useState("lastTwo");
  const [searchResults, setSearchResults] = useState(null);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Form State
  const [orderBody, setOrderBody] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    deliverChannel: "online",
    userId: user?.userId,
    userNo: user?.userNo || "",
    bank: {
      name: user?.bank?.name || "",
      accNo: user?.bank?.accNo || "",
      accName: user?.bank?.accName || "",
    },
    address: user?.address?.addr
      ? `${user.address.addr} ${user.address.province || ""} ${user.address.zipcode || ""}`.trim()
      : "",
    expire: configs?.expireOrder || 30,
  });

  useEffect(() => {
    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCart = async () => {
    const result = await callApi(
      "utils-calculatePrice",
      { userId: user.userId },
      { notShowLoading: true }
    );
    setCart(result);
  };

  const handleSearch = async () => {
    setSearchResults(null);
    setLoading(true);

    let digits = {};
    const val = lottoNumber.trim();
    if (val !== "") {
      if (searchType === "lastTwo") {
        digits = {
          d5: val.slice(-2, -1),
          d6: val.slice(-1),
        };
      } else if (searchType === "lastThree") {
        digits = {
          d4: val.slice(-3, -2),
          d5: val.slice(-2, -1),
          d6: val.slice(-1),
        };
      } else if (searchType === "firstThree") {
        digits = {
          d1: val.slice(0, 1),
          d2: val.slice(1, 2),
          d3: val.slice(2, 3),
        };
      } else {
        digits = {
          d1: val.slice(0, 1),
          d2: val.slice(1, 2),
          d3: val.slice(2, 3),
          d4: val.slice(3, 4),
          d5: val.slice(4, 5),
          d6: val.slice(5, 6),
        };
      }
    }

    const results = await callApi(
      "blocks-searchBlocks",
      {
        digits,
        size: 0,
        limit: 25,
      },
      { notShowLoading: true }
    );

    setLoading(false);
    setSearchResults(results || []);
  };

  const handleDeliverChange = async (type) => {
    setOrderBody((prev) => ({ ...prev, deliverChannel: type }));
    setActionLoading(true);
    await callApi(
      "carts-updateDelivery",
      {
        guestId: user.userId,
        deliverChannel: type,
      },
      { notShowLoading: true }
    );
    setActionLoading(false);
    await fetchCart();
  };

  const handleAddToCart = async (number) => {
    setActionLoading(true);
    const { status, message } = await callApi(
      "carts-addCart",
      {
        number,
        userId: user.userId,
        deliverChannel: orderBody.deliverChannel,
      },
      { notShowLoading: true }
    );

    if (status) {
      toast({
        className: "bg-green text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaCheckCircle /> ใส่ตะกร้าเลข {number} เรียบร้อยแล้ว
          </div>
        ),
      });
      await fetchCart();
      await handleSearch();
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaTimes /> {message || "ไม่สามารถใส่ตะกร้าได้"}
          </div>
        ),
      });
    }
    setActionLoading(false);
  };

  const handleRemoveFromCart = async (number) => {
    setActionLoading(true);
    await callApi(
      "carts-removeCart",
      {
        number,
        guestId: false,
        userId: user.userId,
        all: false,
      },
      { notShowLoading: true }
    );
    setActionLoading(false);
    await fetchCart();
  };

  const handleClearCart = async () => {
    if (!cart?.totalSize) return;
    if (!window.confirm("ยืนยันต้องการล้างสลากทั้งหมดออกจากตะกร้าลูกค้าหรือไม่?")) {
      return;
    }
    setActionLoading(true);
    const result = await callApi(
      "carts-clearCartById",
      { cartRef: user.userId },
      { notShowLoading: true }
    );
    if (result?.status !== false) {
      toast({
        className: "bg-green text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaCheckCircle /> ล้างตะกร้าเรียบร้อยแล้ว
          </div>
        ),
      });
      await fetchCart();
      if (searchResults) await handleSearch();
    }
    setActionLoading(false);
  };

  const handleSubmitOrder = async () => {
    setConfirmModalOpen(false);
    setActionLoading(true);

    const { status, message, orderId } = await callApi(
      "orders-createAdminOrder",
      orderBody
    );

    if (status) {
      toast({
        className: "bg-green text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaCheckCircle /> สร้างคำสั่งซื้อสำเร็จ (รหัส: {orderId})
          </div>
        ),
      });
      window.open(`/orders/${orderId}`, "_blank");
      window.location.reload();
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaTimes /> {message || "ไม่สามารถสร้างคำสั่งซื้อได้"}
          </div>
        ),
      });
    }
    setActionLoading(false);
  };

  const isLottoInCart = (number) => {
    if (!cart?.lotteries) return false;
    return cart.lotteries.some((item) => item.number === number);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
      {/* ── LEFT COLUMN: SEARCH LOTTERY ── */}
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FiSearch className="text-blue text-lg" />
            <h3 className="font-bold text-base text-slate-800">ค้นหาสลากบนแผง (ในระบบ)</h3>
          </div>
          <span className="text-xs text-slate-400">งวด {configs?.roundDate || "-"}</span>
        </div>

        {/* Search Controls */}
        <div className="flex flex-wrap items-end gap-3 bg-slate-50 p-3 rounded-md border border-slate-200">
          <div className="w-32">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              ค้นหาตัวเลข
            </label>
            <input
              className="border border-gray-300 w-full py-1.5 px-3 font-mono font-semibold text-sm rounded-md placeholder:text-xs focus:ring-1 focus:ring-blue focus:border-blue outline-none"
              type="text"
              placeholder="เช่น 99"
              maxLength={6}
              value={lottoNumber}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || validate.digitOnly.test(val)) {
                  setLottoNumber(val);
                }
              }}
            />
          </div>

          <div className="w-36">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              ตำแหน่งเลข
            </label>
            <select
              className="border border-gray-300 w-full py-1.5 px-2 text-xs font-semibold rounded-md bg-white focus:ring-1 focus:ring-blue focus:border-blue outline-none"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="lastTwo">เลขท้าย 2 ตัว</option>
              <option value="lastThree">เลขท้าย 3 ตัว</option>
              <option value="firstThree">เลขหน้า 3 ตัว</option>
              <option value="number">เลข 6 หลัก</option>
            </select>
          </div>

          <button
            type="button"
            disabled={loading || actionLoading}
            className="flex-1 bg-blue text-white py-2 px-4 rounded-md font-semibold text-sm hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 min-w-[100px]"
            onClick={handleSearch}
          >
            {loading ? <Spinner className="w-4 h-4 text-white" /> : <FiSearch />}
            <span>ค้นหา</span>
          </button>
        </div>

        {/* Search Results Table */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 px-1">
            <span>ผลการค้นหา {searchResults ? `(${searchResults.length} รายการ)` : ""}</span>
          </div>

          {loading ? (
            <div className="h-[480px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50">
              <Spinner className="w-6 h-6 text-blue" />
              <p className="text-xs text-slate-400">กำลังค้นหาสลาก...</p>
            </div>
          ) : searchResults ? (
            searchResults.length > 0 ? (
              <ScrollArea className="h-[480px] border border-slate-200 rounded-md">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-100 z-10">
                    <TableRow>
                      <TableHead className="text-center w-12 font-bold">#</TableHead>
                      <TableHead className="text-center font-bold">หมายเลขสลาก</TableHead>
                      <TableHead className="text-center font-bold">จำนวนคงเหลือ</TableHead>
                      <TableHead className="text-right font-bold pr-4">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.map((lotto, index) => {
                      const inCart = isLottoInCart(lotto.number);
                      return (
                        <TableRow key={lotto.number || index} className="hover:bg-slate-50">
                          <TableCell className="text-center font-mono text-xs text-slate-400">
                            {index + 1}
                          </TableCell>
                          <TableCell className="text-center font-mono font-bold text-base text-slate-800">
                            {lotto.number}
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                lotto.size > 1
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {lotto.size > 1 ? `ชุด ${lotto.size} ใบ` : "ใบเดี่ยว (1 ใบ)"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right pr-4">
                            <button
                              type="button"
                              disabled={inCart || actionLoading}
                              className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-semibold shadow-sm transition-all ${
                                inCart
                                  ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                                  : "bg-green text-white hover:opacity-90 active:scale-95"
                              }`}
                              onClick={() => handleAddToCart(lotto.number)}
                            >
                              {inCart ? (
                                <>
                                  <FiCheck className="text-emerald-600" /> อยู่ในตะกร้า
                                </>
                              ) : (
                                <>
                                  <FiPlus /> ใส่ตะกร้า
                                </>
                              )}
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="h-[480px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
                <FiSearch className="text-3xl text-slate-300" />
                <p className="text-sm font-semibold">ไม่พบสลากที่ค้นหา</p>
                <p className="text-xs">ลองค้นหาด้วยเลขอื่น หรือตรวจสอบเลขที่แผง</p>
              </div>
            )
          ) : (
            <div className="h-[480px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
              <FiSearch className="text-3xl text-slate-300" />
              <p className="text-sm font-semibold">พิมพ์เลขแล้วกดค้นหา</p>
              <p className="text-xs">ผลการค้นหาจากแผงสลากจะแสดงตรงนี้</p>
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT COLUMN: CART & ORDER CONFIG ── */}
      <div className="p-4 space-y-4 flex flex-col justify-between">
        <div className="space-y-4">
          {/* Cart Header */}
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <FiShoppingCart className="text-green text-lg" />
              <h3 className="font-bold text-base text-slate-800">
                ตะกร้าสลากของลูกค้า
              </h3>
              <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full font-bold">
                {cart?.totalSize || 0} ใบ
              </span>
            </div>

            {cart?.lotteries?.length > 0 && (
              <button
                type="button"
                disabled={actionLoading}
                className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1 hover:underline disabled:opacity-50"
                onClick={handleClearCart}
              >
                <FiTrash2 /> ล้างตะกร้าทั้งหมด
              </button>
            )}
          </div>

          {/* Cart Table */}
          {cart?.lotteries?.length > 0 ? (
            <ScrollArea className="h-[220px] border border-slate-200 rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead className="font-bold">หมายเลขสลาก</TableHead>
                    <TableHead className="text-right font-bold">ค่าเลขพิเศษ</TableHead>
                    <TableHead className="text-right font-bold">ราคา</TableHead>
                    <TableHead className="w-12 text-center"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cart.lotteries.map((item) => (
                    <TableRow key={item.number} className="hover:bg-slate-50">
                      <TableCell className="font-mono font-bold text-sm text-slate-800">
                        {item.number}
                      </TableCell>
                      <TableCell className="text-right text-xs font-semibold text-amber-600">
                        {item.specialNumberFee ? `+ ${item.specialNumberFee} ฿` : "-"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold text-slate-700">
                        {(item.price + (item.specialNumberFee || 0)).toLocaleString()} ฿
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          type="button"
                          disabled={actionLoading}
                          className="text-rose-500 hover:text-rose-700 p-1.5 rounded hover:bg-rose-50 transition-all disabled:opacity-40"
                          onClick={() => handleRemoveFromCart(item.number)}
                          title="ลบออกจากตะกร้า"
                        >
                          <FiTrash2 className="text-base" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="h-[220px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
              <FiShoppingCart className="text-3xl text-slate-300" />
              <p className="text-sm font-semibold">ยังไม่มีสลากในตะกร้า</p>
              <p className="text-xs">ค้นหาสลากทางซ้ายมือแล้วกด "ใส่ตะกร้า"</p>
            </div>
          )}

          {/* Price Breakdown Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-600 text-xs">
              <span>ค่าสลาก ({cart?.totalSize || 0} ใบ)</span>
              <span className="font-semibold">
                {((configs?.price || 80) * (cart?.totalSize || 0)).toLocaleString()} ฿
              </span>
            </div>

            {orderBody.deliverChannel === "online" && (
              <div className="flex justify-between text-slate-600 text-xs">
                <span>ค่าเช่าพื้นที่ฝากพร้อมบริการฯ</span>
                <span className="font-semibold">
                  {((configs?.fee || 20) * (cart?.totalSize || 0)).toLocaleString()} ฿
                </span>
              </div>
            )}

            {orderBody.deliverChannel === "mail" && (
              <div className="flex justify-between text-slate-600 text-xs">
                <span>ค่าจัดส่งด่วน EMS</span>
                <span className="font-semibold">
                  {(cart?.totalShippingPrice || 0).toLocaleString()} ฿
                </span>
              </div>
            )}

            <div className="border-t border-slate-200 pt-1.5 flex justify-between items-center">
              <span className="font-bold text-slate-800">ยอดรวมสุทธิ</span>
              <span className="text-lg font-extrabold text-green">
                {(cart?.totalPrice || 0).toLocaleString()} ฿
              </span>
            </div>
          </div>

          {/* Order Settings Section */}
          <div className="space-y-3 pt-2">
            <h4 className="font-bold text-sm text-slate-700 flex items-center gap-1.5">
              <FiTruck className="text-blue" /> ช่องทางการรับสลาก
            </h4>

            {/* Delivery Channel Radio Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {DELIVERY_CHANNELS.map((ch) => {
                const selected = orderBody.deliverChannel === ch.id;
                return (
                  <button
                    key={ch.id}
                    type="button"
                    className={`text-left p-2.5 rounded-md border transition-all ${
                      selected
                        ? "border-blue bg-blue/5 ring-1 ring-blue"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                    onClick={() => handleDeliverChange(ch.id)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {ch.icon}
                        <span className="font-bold text-xs text-slate-800">
                          {ch.title}
                        </span>
                      </div>
                      {selected && <FiCheck className="text-blue text-sm" />}
                    </div>
                    <p className="text-[11px] text-slate-500 line-clamp-1">
                      {ch.subTitle}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* If EMS is selected: Shipping Address Override */}
            {orderBody.deliverChannel === "mail" && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                  <FiMapPin /> ที่อยู่สำหรับจัดส่ง EMS ครั้งนี้
                </label>
                <textarea
                  rows={2}
                  className="w-full border border-amber-300 rounded-md p-2 text-xs font-medium text-slate-700 bg-white focus:ring-1 focus:ring-amber-500 outline-none"
                  placeholder="ระบุที่อยู่จัดส่ง (บ้านเลขที่, ถนน, ตำบล, อำเภอ, จังหวัด, รหัสไปรษณีย์)"
                  value={orderBody.address}
                  onChange={(e) =>
                    setOrderBody((prev) => ({ ...prev, address: e.target.value }))
                  }
                />
              </div>
            )}

            {/* Expiry & Buyer Inputs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  ออเดอร์หมดอายุใน (นาที)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className="border border-gray-300 py-1.5 px-2.5 text-xs font-semibold rounded-md w-24 outline-none focus:border-blue"
                    placeholder="ระบุนาที เช่น 30"
                    value={orderBody.expire}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || validate.digitOnly.test(val)) {
                        setOrderBody((prev) => ({ ...prev, expire: val }));
                      }
                    }}
                  />
                  <div className="flex gap-1">
                    {EXPIRE_PRESETS.map((min) => (
                      <button
                        key={min}
                        type="button"
                        className={`text-[11px] px-2 py-1 rounded border font-semibold ${
                          Number(orderBody.expire) === min
                            ? "bg-blue text-white border-blue"
                            : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                        }`}
                        onClick={() =>
                          setOrderBody((prev) => ({ ...prev, expire: min }))
                        }
                      >
                        {min >= 60 ? `${min / 60} ชม.` : `${min}น.`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  เบอร์โทรผู้ซื้อ
                </label>
                <input
                  type="text"
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md outline-none focus:border-blue"
                  value={orderBody.phone}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || validate.digitOnly.test(val)) {
                      setOrderBody((prev) => ({ ...prev, phone: val }));
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  ชื่อ-นามสกุล
                </label>
                <input
                  type="text"
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md outline-none focus:border-blue"
                  value={`${orderBody.firstName} ${orderBody.lastName}`}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-200">
          <button
            type="button"
            disabled={!cart?.totalSize || actionLoading}
            className="w-full bg-green text-white py-3 px-4 rounded-md font-bold text-base hover:opacity-90 shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => setConfirmModalOpen(true)}
          >
            {actionLoading ? (
              <Spinner className="w-5 h-5 text-white" />
            ) : (
              <>
                <IoReceiptOutline className="text-xl" />
                <span>
                  สร้างออเดอร์ ({cart?.totalSize || 0} ใบ) —{" "}
                  {(cart?.totalPrice || 0).toLocaleString()} ฿
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModalOpen && (
        <OrderConfirmationModal
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleSubmitOrder}
          loading={actionLoading}
          user={user}
          orderBody={orderBody}
          totalSize={cart?.totalSize || 0}
          totalPrice={cart?.totalPrice || 0}
          type="onBlock"
        />
      )}
    </div>
  );
};

// ── Tab 2: Off-Block Picker Component (สลากนอกระบบ) ───────────────────────────
export const NonBlockLotteryPicker = ({ user = {} }) => {
  const { toast } = useToast();
  const { callApi, configs } = Context();

  const [lottoNumber, setLottoNumber] = useState("");
  const [searchType, setSearchType] = useState("lastTwo");
  const [searchResults, setSearchResults] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Form State
  const [orderBody, setOrderBody] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    deliverChannel: "online",
    userId: user?.userId,
    userNo: user?.userNo || "",
    lineId: user?.lineId || "",
    bank: {
      name: user?.bank?.name || "",
      accNo: user?.bank?.accNo || "",
      accName: user?.bank?.accName || "",
    },
    address: user?.address?.addr
      ? `${user.address.addr} ${user.address.province || ""} ${user.address.zipcode || ""}`.trim()
      : "",
    status: statuses.PENDING,
    expire: configs?.expireOrder || 30,
    totalPrice: 0,
  });

  const searchLotto = async () => {
    setSearchResults(null);
    setLoading(true);

    const queries = [
      ["isSold", false],
      ["isOnBlock", false],
      ["roundDate", configs?.roundDate],
    ];

    const val = lottoNumber.trim();
    if (val !== "") {
      switch (searchType) {
        case "lastTwo":
          queries.push(["lastTwo", val]);
          break;
        case "lastThree":
          queries.push(["lastThree", val]);
          break;
        case "firstThree":
          queries.push(["firstThree", val]);
          break;
        case "number":
          queries.push(["number", val]);
          break;
        case "fileNameId":
          queries.push(["fileNameId", val]);
          break;
        default:
          break;
      }
    }

    const results = await callApi(
      "lotteries-getAll",
      {
        queries,
        limit: 200,
        roundDate: configs?.roundDate,
      },
      { notShowLoading: true }
    );

    setLoading(false);
    setSearchResults(results || []);
  };

  const handleAddAllToCart = () => {
    if (!searchResults?.length) return;
    const allIds = searchResults.map((item) => item.id);
    setCart((prev) => [...new Set([...prev, ...allIds])]);
    toast({
      className: "bg-green text-white",
      title: (
        <div className="flex gap-2 items-center text-sm font-semibold">
          <FaCheckCircle /> เพิ่มสลากทั้งหมด ({allIds.length} ใบ) เข้าตะกร้าแล้ว
        </div>
      ),
    });
  };

  const handleAddToCart = (id) => {
    if (cart.includes(id)) return;
    setCart((prev) => [...prev, id]);
  };

  const handleRemoveFromCart = (id) => {
    setCart((prev) => prev.filter((item) => item !== id));
  };

  const handleClearCart = () => {
    if (!cart.length) return;
    if (window.confirm("ยืนยันต้องการล้างสลากทั้งหมดออกจากตะกร้าหรือไม่?")) {
      setCart([]);
    }
  };

  // Price calculations
  const priceCalculations = useMemo(() => {
    const qty = cart.length;
    const ticketPrice = (configs?.price || 80) * qty;
    const fee = orderBody.deliverChannel === "online" ? (configs?.fee || 20) * qty : 0;
    const shipping =
      orderBody.deliverChannel === "mail"
        ? (configs?.shipping?.prices?.[0] || 50) +
          Math.max(0, qty - 1) * (configs?.shipping?.prices?.[1] || 10)
        : 0;
    const total = ticketPrice + fee + shipping;
    return { ticketPrice, fee, shipping, total, qty };
  }, [cart.length, orderBody.deliverChannel, configs]);

  const handleSubmitOrder = async () => {
    setConfirmModalOpen(false);
    setActionLoading(true);

    const { status, message, orderId } = await callApi(
      "orders-createAdminOffBlockOrder",
      {
        ...orderBody,
        lotteryIds: cart,
        totalPrice: priceCalculations.total,
      }
    );

    if (status) {
      toast({
        className: "bg-green text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaCheckCircle /> สร้างคำสั่งซื้อสำเร็จ (รหัส: {orderId})
          </div>
        ),
      });
      window.open(`/orders/${orderId}`, "_blank");
      window.location.reload();
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaTimes /> {message || "ไม่สามารถสร้างคำสั่งซื้อได้"}
          </div>
        ),
      });
    }
    setActionLoading(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
      {/* ── LEFT COLUMN: SEARCH OFF-BLOCK LOTTERY ── */}
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FiSearch className="text-blue text-lg" />
            <h3 className="font-bold text-base text-slate-800">ค้นหาสลากคลังหลังบ้าน (นอกระบบ)</h3>
          </div>
          <span className="text-xs text-slate-400">งวด {configs?.roundDate || "-"}</span>
        </div>

        {/* Search Controls */}
        <div className="flex flex-wrap items-end gap-3 bg-slate-50 p-3 rounded-md border border-slate-200">
          <div className="w-32">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              ค้นหาตัวเลข/ชื่อเล่ม
            </label>
            <input
              className="border border-gray-300 w-full py-1.5 px-3 font-mono font-semibold text-sm rounded-md placeholder:text-xs focus:ring-1 focus:ring-blue focus:border-blue outline-none"
              type="text"
              placeholder="000000"
              value={lottoNumber}
              onKeyDown={(e) => {
                if (e.key === "Enter") searchLotto();
              }}
              onChange={(e) => setLottoNumber(e.target.value)}
            />
          </div>

          <div className="w-36">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              ประเภทค้นหา
            </label>
            <select
              className="border border-gray-300 w-full py-1.5 px-2 text-xs font-semibold rounded-md bg-white focus:ring-1 focus:ring-blue focus:border-blue outline-none"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="lastTwo">เลขท้าย 2 ตัว</option>
              <option value="lastThree">เลขท้าย 3 ตัว</option>
              <option value="firstThree">เลขหน้า 3 ตัว</option>
              <option value="number">เลข 6 หลัก</option>
              <option value="fileNameId">ชื่อเล่ม / แฟ้ม</option>
            </select>
          </div>

          <button
            type="button"
            disabled={loading || actionLoading}
            className="flex-1 bg-blue text-white py-2 px-4 rounded-md font-semibold text-sm hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 min-w-[100px]"
            onClick={searchLotto}
          >
            {loading ? <Spinner className="w-4 h-4 text-white" /> : <FiSearch />}
            <span>ค้นหา</span>
          </button>
        </div>

        {/* Search Results Table */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 px-1">
            <span>ผลการค้นหา {searchResults ? `(${searchResults.length} ใบ)` : ""}</span>

            {searchResults?.length > 0 && (
              <button
                type="button"
                disabled={actionLoading}
                className="text-xs bg-emerald-600 text-white font-semibold py-1 px-3 rounded shadow-sm hover:bg-emerald-700 transition-all flex items-center gap-1"
                onClick={handleAddAllToCart}
              >
                <FiPlus /> ใส่ตะกร้าทั้งหมด ({searchResults.length} ใบ)
              </button>
            )}
          </div>

          {loading ? (
            <div className="h-[480px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50">
              <Spinner className="w-6 h-6 text-blue" />
              <p className="text-xs text-slate-400">กำลังค้นหาสลากคลังหลังบ้าน...</p>
            </div>
          ) : searchResults ? (
            searchResults.length > 0 ? (
              <ScrollArea className="h-[480px] border border-slate-200 rounded-md">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-100 z-10">
                    <TableRow>
                      <TableHead className="text-center font-bold">หมายเลขสลาก</TableHead>
                      <TableHead className="text-center font-bold">ชื่อเล่ม</TableHead>
                      <TableHead className="text-right font-bold pr-4">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.map((lotto) => {
                      const inCart = cart.includes(lotto.id);
                      return (
                        <TableRow key={lotto.id} className="hover:bg-slate-50">
                          <TableCell className="text-center font-mono font-bold text-base text-slate-800">
                            {lotto.id}
                          </TableCell>
                          <TableCell className="text-center text-xs font-semibold text-slate-500">
                            {lotto.fileNameId || "-"}
                          </TableCell>
                          <TableCell className="text-right pr-4">
                            <button
                              type="button"
                              disabled={inCart || actionLoading}
                              className={`inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-semibold shadow-sm transition-all ${
                                inCart
                                  ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                                  : "bg-green text-white hover:opacity-90 active:scale-95"
                              }`}
                              onClick={() => handleAddToCart(lotto.id)}
                            >
                              {inCart ? (
                                <>
                                  <FiCheck className="text-emerald-600" /> อยู่ในตะกร้า
                                </>
                              ) : (
                                <>
                                  <FiPlus /> ใส่ตะกร้า
                                </>
                              )}
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="h-[480px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
                <FiSearch className="text-3xl text-slate-300" />
                <p className="text-sm font-semibold">ไม่พบสลากที่ยังไม่ขาย</p>
                <p className="text-xs">สลากอาจถูกขายไปแล้ว หรือถูกนำขึ้นแผงในระบบ</p>
              </div>
            )
          ) : (
            <div className="h-[480px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
              <FiSearch className="text-3xl text-slate-300" />
              <p className="text-sm font-semibold">ค้นหาสลากคลังหลังบ้าน</p>
              <p className="text-xs">ค้นหาด้วยเลข หรือพิมพ์ชื่อเล่มเพื่อดึงสลากทั้งเล่ม</p>
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT COLUMN: CART & ORDER CONFIG ── */}
      <div className="p-4 space-y-4 flex flex-col justify-between">
        <div className="space-y-4">
          {/* Cart Header */}
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <FiShoppingCart className="text-green text-lg" />
              <h3 className="font-bold text-base text-slate-800">
                สลากที่เลือกในตะกร้า
              </h3>
              <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full font-bold">
                {cart.length} ใบ
              </span>
            </div>

            {cart.length > 0 && (
              <button
                type="button"
                disabled={actionLoading}
                className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1 hover:underline disabled:opacity-50"
                onClick={handleClearCart}
              >
                <FiTrash2 /> ล้างตะกร้าทั้งหมด
              </button>
            )}
          </div>

          {/* Cart Table */}
          {cart.length > 0 ? (
            <ScrollArea className="h-[220px] border border-slate-200 rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead className="w-12 text-center font-bold">ลำดับ</TableHead>
                    <TableHead className="font-bold">หมายเลขสลาก</TableHead>
                    <TableHead className="text-center font-bold">จำนวน</TableHead>
                    <TableHead className="w-12 text-center"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cart.map((id, index) => (
                    <TableRow key={id} className="hover:bg-slate-50">
                      <TableCell className="text-center font-mono text-xs text-slate-400">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-mono font-bold text-sm text-slate-800">
                        {id}
                      </TableCell>
                      <TableCell className="text-center text-xs font-semibold text-slate-600">
                        1 ใบ
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          type="button"
                          disabled={actionLoading}
                          className="text-rose-500 hover:text-rose-700 p-1.5 rounded hover:bg-rose-50 transition-all disabled:opacity-40"
                          onClick={() => handleRemoveFromCart(id)}
                          title="ลบออกจากตะกร้า"
                        >
                          <FiTrash2 className="text-base" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="h-[220px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
              <FiShoppingCart className="text-3xl text-slate-300" />
              <p className="text-sm font-semibold">ยังไม่มีสลากในตะกร้า</p>
              <p className="text-xs">ค้นหาแล้วกด "ใส่ตะกร้า" หรือ "ใส่ตะกร้าทั้งหมด"</p>
            </div>
          )}

          {/* Live Price Breakdown Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-600 text-xs">
              <span>ค่าสลาก ({priceCalculations.qty} ใบ)</span>
              <span className="font-semibold">
                {priceCalculations.ticketPrice.toLocaleString()} ฿
              </span>
            </div>

            {orderBody.deliverChannel === "online" && (
              <div className="flex justify-between text-slate-600 text-xs">
                <span>ค่าเช่าพื้นที่ฝากพร้อมบริการฯ</span>
                <span className="font-semibold">
                  {priceCalculations.fee.toLocaleString()} ฿
                </span>
              </div>
            )}

            {orderBody.deliverChannel === "mail" && (
              <div className="flex justify-between text-slate-600 text-xs">
                <span>ค่าจัดส่งด่วน EMS</span>
                <span className="font-semibold">
                  {priceCalculations.shipping.toLocaleString()} ฿
                </span>
              </div>
            )}

            <div className="border-t border-slate-200 pt-1.5 flex justify-between items-center">
              <span className="font-bold text-slate-800">ยอดรวมสุทธิ</span>
              <span className="text-lg font-extrabold text-green">
                {priceCalculations.total.toLocaleString()} ฿
              </span>
            </div>
          </div>

          {/* Order Settings Section */}
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Channel */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  ช่องทางการรับสลาก
                </label>
                <select
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md bg-white focus:ring-1 focus:ring-blue outline-none"
                  value={orderBody.deliverChannel}
                  onChange={(e) =>
                    setOrderBody((prev) => ({ ...prev, deliverChannel: e.target.value }))
                  }
                >
                  <option value="online">เช่าพื้นที่พร้อมบริการฯ (Online)</option>
                  <option value="mail">ส่งด่วน EMS</option>
                  <option value="offline">ลอตเตอรี่ 80 บาท</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  สถานะออเดอร์เริ่มต้น
                </label>
                <select
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md bg-white focus:ring-1 focus:ring-blue outline-none"
                  value={orderBody.status}
                  onChange={(e) =>
                    setOrderBody((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value={statuses.APPROVE}>อนุมัติทันที (Approved)</option>
                  <option value={statuses.PAID}>ชำระแล้ว รออนุมัติ (Paid)</option>
                  <option value={statuses.PENDING}>รอชำระเงิน (Pending)</option>
                  <option value={statuses.CANCEL}>ยกเลิก (Canceled)</option>
                </select>
              </div>
            </div>

            {/* If EMS is selected: Shipping Address Override */}
            {orderBody.deliverChannel === "mail" && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                  <FiMapPin /> ที่อยู่สำหรับจัดส่ง EMS ครั้งนี้
                </label>
                <textarea
                  rows={2}
                  className="w-full border border-amber-300 rounded-md p-2 text-xs font-medium text-slate-700 bg-white focus:ring-1 focus:ring-amber-500 outline-none"
                  placeholder="ระบุที่อยู่จัดส่ง (บ้านเลขที่, ถนน, ตำบล, อำเภอ, จังหวัด, รหัสไปรษณีย์)"
                  value={orderBody.address}
                  onChange={(e) =>
                    setOrderBody((prev) => ({ ...prev, address: e.target.value }))
                  }
                />
              </div>
            )}

            {/* Buyer & Bank Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  เบอร์โทรผู้ซื้อ
                </label>
                <input
                  type="text"
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md outline-none focus:border-blue"
                  value={orderBody.phone}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || validate.digitOnly.test(val)) {
                      setOrderBody((prev) => ({ ...prev, phone: val }));
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  ธนาคารผู้รับ
                </label>
                <select
                  className="border border-gray-300 w-full py-1.5 px-2 text-xs font-semibold rounded-md bg-white focus:ring-1 focus:ring-blue outline-none"
                  value={orderBody.bank.name}
                  onChange={(e) =>
                    setOrderBody((prev) => ({
                      ...prev,
                      bank: { ...prev.bank, name: e.target.value },
                    }))
                  }
                >
                  <option value="">-</option>
                  {constant.bankList.map((b) => (
                    <option key={b.bankName} value={b.bankName}>
                      {b.bankName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  ชื่อบัญชี
                </label>
                <input
                  type="text"
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md outline-none focus:border-blue"
                  value={orderBody.bank.accName}
                  onChange={(e) =>
                    setOrderBody((prev) => ({
                      ...prev,
                      bank: { ...prev.bank, accName: e.target.value },
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  เลขบัญชี
                </label>
                <input
                  type="text"
                  className="border border-gray-300 w-full py-1.5 px-2.5 text-xs font-semibold rounded-md outline-none focus:border-blue"
                  value={orderBody.bank.accNo}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || validate.digitOnly.test(val)) {
                      setOrderBody((prev) => ({
                        ...prev,
                        bank: { ...prev.bank, accNo: val },
                      }));
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-200">
          <button
            type="button"
            disabled={!cart.length || actionLoading}
            className="w-full bg-green text-white py-3 px-4 rounded-md font-bold text-base hover:opacity-90 shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => setConfirmModalOpen(true)}
          >
            {actionLoading ? (
              <Spinner className="w-5 h-5 text-white" />
            ) : (
              <>
                <IoReceiptOutline className="text-xl" />
                <span>
                  สร้างออเดอร์นอกระบบ ({cart.length} ใบ) —{" "}
                  {priceCalculations.total.toLocaleString()} ฿
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModalOpen && (
        <OrderConfirmationModal
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleSubmitOrder}
          loading={actionLoading}
          user={user}
          orderBody={orderBody}
          totalSize={priceCalculations.qty}
          totalPrice={priceCalculations.total}
          type="offBlock"
        />
      )}
    </div>
  );
};

// ── Order Confirmation Modal Component ─────────────────────────────────────────
const OrderConfirmationModal = ({
  onClose,
  onConfirm,
  loading,
  user,
  orderBody,
  totalSize,
  totalPrice,
  type = "onBlock",
}) => {
  const channelText =
    orderBody.deliverChannel === "online"
      ? "เช่าพื้นที่พร้อมบริการฯ (Online)"
      : orderBody.deliverChannel === "mail"
      ? "ส่งด่วน EMS"
      : "ลอตเตอรี่ 80 บาท (Offline)";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-150">
        {/* Modal Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiCheckCircle className="text-emerald-600 text-lg" />
            <h3 className="font-bold text-base text-slate-800">
              ยืนยันการสร้างคำสั่งซื้อ
            </h3>
          </div>
          <button
            type="button"
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 p-1 rounded"
            onClick={onClose}
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 space-y-3.5 text-sm">
          <div className="bg-slate-50 p-3 rounded-md border border-slate-200 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">ลูกค้าผู้ซื้อ:</span>
              <span className="font-bold text-slate-800">
                {user?.firstName} {user?.lastName} ({user?.userId})
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">เบอร์โทรศัพท์:</span>
              <span className="font-mono font-semibold text-slate-800">
                {orderBody.phone || user?.phone || "-"}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">ประเภทสลาก:</span>
              <span className="font-semibold text-blue">
                {type === "onBlock" ? "สลากในระบบ (หน้าบ้าน)" : "สลากนอกระบบ (หลังบ้าน)"}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">ช่องทางรับสลาก:</span>
              <span className="font-semibold text-slate-800">{channelText}</span>
            </div>
            {type === "offBlock" && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">สถานะเริ่มต้น:</span>
                <span className="font-semibold text-amber-600">
                  {orderBody.status}
                </span>
              </div>
            )}
          </div>

          <div className="border border-emerald-100 bg-emerald-50/50 p-3.5 rounded-md flex justify-between items-center">
            <div>
              <p className="text-xs text-slate-500">จำนวนทั้งหมด</p>
              <p className="text-base font-bold text-slate-800">{totalSize} ใบ</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">ยอดชำระสุทธิ</p>
              <p className="text-xl font-extrabold text-green">
                {totalPrice.toLocaleString()} ฿
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex gap-2 justify-end">
          <button
            type="button"
            disabled={loading}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-md transition-all"
            onClick={onClose}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            disabled={loading}
            className="px-5 py-2 text-xs font-bold text-white bg-green hover:opacity-90 rounded-md shadow-sm transition-all flex items-center gap-1.5"
            onClick={onConfirm}
          >
            {loading ? <Spinner className="w-4 h-4 text-white" /> : <FiCheck />}
            <span>ยืนยันสร้างคำสั่งซื้อ</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOrder;
